/**
 * The one place that talks to resto-api. Port of `core/cloud/cloud_client.dart` +
 * `core/api_exception.dart`, with Auth0 in place of the app's own OTP tokens.
 *
 * Paths are relative to `REACT_APP_API_SERVER_URL`, which points at resto-api's `/api/resto`
 * (`https://resto.innpro.in/api/resto` in production — the proxy does not rewrite the prefix). So
 * `get('/outlets')` hits `https://resto.innpro.in/api/resto/outlets`.
 *
 * The bearer is the **Auth0 ID token**: resto-api's `AuthenticationConverter` resolves the account
 * from the `email` claim, which only the ID token carries. There is no refresh-token dance here —
 * Auth0 renews the session, and a 401 means "this account may not use the cloud API".
 */
import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useMemo } from 'react';
import { trNow } from '../i18n/l10n';
import { webStrings } from '../i18n/web-strings';

const API_URL = process.env.REACT_APP_API_SERVER_URL;

/** resto-api errors: Spring `ProblemDetail` / `ResponseStatusException`, or a hub-style `Problem`. */
export interface Problem {
    code: string;
    message: string;
    details: Record<string, any>;
}

function problemOf(status: number, body: string): Problem {
    try {
        const json = JSON.parse(body);
        if (json && typeof json === 'object') {
            const code = json.code ?? json.error ?? json.title ?? `HTTP_${status}`;
            const message = json.message ?? json.detail ?? json.title ?? code;
            return { code: String(code), message: String(message), details: json.details ?? {} };
        }
    } catch {
        /* not JSON — fall through to the raw body */
    }
    return {
        code: `HTTP_${status}`,
        message: body.trim() === '' ? `HTTP ${status}` : body.trim(),
        details: {},
    };
}

/** Thrown for any non-2xx response, and for a network failure (`status === 0`). */
export class ApiError extends Error {
    readonly status: number;
    readonly code: string;
    readonly details: Record<string, any>;

    constructor(status: number, problem: Problem) {
        super(problem.message);
        this.name = 'ApiError';
        this.status = status;
        this.code = problem.code;
        this.details = problem.details;
    }

    /** No route to the server (offline, API down, CORS). */
    get isOffline(): boolean {
        return this.status === 0;
    }

    /** resto-api answers 501 for an endpoint that is not built yet — show a banner, never crash. */
    get isNotImplemented(): boolean {
        return this.status === 501;
    }

    get isForbidden(): boolean {
        return this.status === 401 || this.status === 403;
    }
}

export const errorText = (e: unknown): string =>
    e instanceof ApiError
        ? e.isOffline
            ? trNow(webStrings, 'errOffline')
            : e.message
        : e instanceof Error
          ? e.message
          : String(e);

type Query = Record<string, string | number | boolean | null | undefined>;

interface RequestOptions {
    query?: Query;
    body?: unknown;
    /** Overrides JSON for raw bodies, e.g. `text/csv` for the menu import. */
    contentType?: string;
    /** `json` (default), `text`, `blob` or `none`. */
    accept?: 'json' | 'text' | 'blob' | 'none';
}

function urlOf(path: string, query?: Query): string {
    const url = new URL(`${API_URL}${path}`, window.location.origin);
    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== null && value !== undefined && value !== '') url.searchParams.set(key, String(value));
    }
    return url.toString();
}

export interface ApiClient {
    get<T = any>(path: string, query?: Query): Promise<T>;
    post<T = any>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
    put<T = any>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
    patch<T = any>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
    del<T = any>(path: string, body?: unknown): Promise<T>;
    /** CSV exports (`format=csv`). */
    text(path: string, query?: Query): Promise<string>;
    /** PDF exports (`format=pdf`). */
    blob(path: string, query?: Query): Promise<Blob>;
    /** `multipart/form-data` POST; `onProgress` gets the fraction (0..1) of the body sent so far. */
    upload<T = any>(path: string, form: FormData, onProgress?: (fraction: number) => void): Promise<T>;
}

/**
 * `useApi()` gives the raw client; `useOwnerApi()` (hooks/useOwnerApi.ts) wraps it with the
 * outlet-scoped calls the screens use.
 */
export function useApi(): ApiClient {
    const { getIdTokenClaims, loginWithRedirect } = useAuth0();

    /** The raw ID token; Auth0 refreshes it in the background, so this is cheap to call per request. */
    const token = useCallback(async (): Promise<string> => {
        const claims = await getIdTokenClaims();
        const raw = claims?.__raw;
        if (!raw) {
            // The session lapsed between renders — send the user back through Auth0.
            await loginWithRedirect();
            throw new ApiError(401, { code: 'NO_SESSION', message: trNow(webStrings, 'errSession'), details: {} });
        }
        return raw;
    }, [getIdTokenClaims, loginWithRedirect]);

    const send = useCallback(
        async <T,>(method: string, path: string, options: RequestOptions = {}): Promise<T> => {
            const { query, body, contentType, accept = 'json' } = options;
            const headers = new Headers({ authorization: `Bearer ${await token()}` });
            let payload: BodyInit | undefined;
            if (body !== undefined && body !== null) {
                if (contentType) {
                    headers.set('content-type', contentType);
                    payload = String(body);
                } else {
                    headers.set('content-type', 'application/json');
                    payload = JSON.stringify(body);
                }
            }
            if (accept === 'blob') headers.set('accept', 'application/pdf');

            let response: Response;
            try {
                response = await fetch(urlOf(path, query), { method, headers, body: payload });
            } catch (e) {
                throw new ApiError(0, { code: 'OFFLINE', message: String((e as Error)?.message ?? e), details: {} });
            }

            if (!response.ok) {
                let text = '';
                try {
                    text = await response.text();
                } catch {
                    /* ignore */
                }
                throw new ApiError(response.status, problemOf(response.status, text));
            }

            if (accept === 'none' || response.status === 204) return undefined as T;
            if (accept === 'blob') return (await response.blob()) as T;
            if (accept === 'text') return (await response.text()) as T;

            const text = await response.text();
            if (text.trim() === '') return undefined as T;
            try {
                return JSON.parse(text) as T;
            } catch {
                return text as unknown as T;
            }
        },
        [token],
    );

    /** `fetch` cannot report upload progress, so file uploads go through XMLHttpRequest. */
    const upload = useCallback(
        async <T,>(path: string, form: FormData, onProgress?: (fraction: number) => void): Promise<T> => {
            const bearer = await token();
            return new Promise<T>((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('POST', urlOf(path));
                // No content-type: the browser adds the multipart boundary itself.
                xhr.setRequestHeader('authorization', `Bearer ${bearer}`);
                xhr.upload.onprogress = (e) => {
                    if (e.lengthComputable && e.total > 0) onProgress?.(e.loaded / e.total);
                };
                xhr.onerror = () =>
                    reject(new ApiError(0, { code: 'OFFLINE', message: trNow(webStrings, 'errNetwork'), details: {} }));
                xhr.onload = () => {
                    const text = xhr.responseText ?? '';
                    if (xhr.status < 200 || xhr.status >= 300) {
                        reject(new ApiError(xhr.status, problemOf(xhr.status, text)));
                        return;
                    }
                    try {
                        resolve((text.trim() === '' ? undefined : JSON.parse(text)) as T);
                    } catch {
                        resolve(text as unknown as T);
                    }
                };
                xhr.send(form);
            });
        },
        [token],
    );

    return useMemo<ApiClient>(
        () => ({
            get: (path, query) => send('GET', path, { query }),
            post: (path, body, options) => send('POST', path, { ...options, body }),
            put: (path, body, options) => send('PUT', path, { ...options, body }),
            patch: (path, body, options) => send('PATCH', path, { ...options, body }),
            del: (path, body) => send('DELETE', path, { body }),
            text: (path, query) => send('GET', path, { query, accept: 'text' }),
            blob: (path, query) => send('GET', path, { query, accept: 'blob' }),
            upload,
        }),
        [send, upload],
    );
}
